import { StanInput, StanModal } from '@/components';
import { ProductPayment } from '../types/transactionTypes';
import { resendOrder } from '@/lib/api/income/api';
import { logWithSentry, stanNotify } from '@/lib';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

interface UndeliveredTransactionModalProps {
    orderId?: string | number;
    orderDetails: ProductPayment;
    isOpen: boolean;
    onClose: () => void;
    onOrderResent: () => void;
}
const emailSchema = z.object({
    email: z.string().email('Please enter a valid email!'),
});

type EmailFormData = z.infer<typeof emailSchema>;

export function UndeliveredTransactionModal({
    orderId,
    orderDetails,
    onClose,
    isOpen,
    onOrderResent,
}: UndeliveredTransactionModalProps) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<EmailFormData>({
        resolver: zodResolver(emailSchema),
        defaultValues: { email: '' },
    });

    const emailToSendTo = watch('email');

    const handleResend = async (data: EmailFormData) => {
        try {
            await resendOrder(data.email, orderId);
            onOrderResent();
            stanNotify({
                type: 'success',
                title: 'Order resend successful',
                text: 'We have resent the order to the fan',
                duration: 5000,
            });
        } catch (error: any) {
            stanNotify({
                type: 'error',
                title: 'Oops',
                text: error?.response?.data?.message || 'Please reach out to us at friends@stanwith.me.',
                duration: 2000,
            });
            logWithSentry('failed-to-resend-delivery', {
                context: { orderId, orderDetails },
            });
        }
        onClose();
    };

    return (
        <StanModal
            id='undelivered-order-modal'
            onClose={onClose}
            isOpen={isOpen}
            title='Undelivered Transaction'
            primaryActionLabel='Resend'
            primaryActionDisabled={!emailToSendTo}
            onPrimaryAction={handleSubmit(handleResend)}
        >
            <div className='p-3'>
                <div className='col-md mb-3 flex items-center'>
                    <div className='mr-3'>
                        <h6 className='m-0 font-bold'>#{orderId}</h6>
                    </div>
                    <div className='font-bold'>{orderDetails.date}</div>
                </div>
                <div className='flex flex-col gap-2'>
                    <div className='flex flex-wrap items-start'>
                        <label className='mr-2 font-bold'>Name:</label>
                        <div>{orderDetails.data.name}</div>
                    </div>

                    <div className='flex flex-wrap items-start'>
                        <label className='mr-2 font-bold'>Email:</label>
                        <div>{orderDetails.data.email}</div>
                    </div>

                    <div className='flex flex-wrap items-start'>
                        <label className='mr-2 font-bold'>Product:</label>
                        <div>{orderDetails.data.title}</div>
                    </div>
                    <div className='flex flex-wrap items-start'>
                        <label className='mr-2 font-bold'>Revenue: </label>
                        <div>{orderDetails.amountFormatted}</div>
                    </div>
                    <div className='flex flex-wrap items-start'>
                        <label className='mr-2 font-bold'>Customer Note:</label>
                        <div>{orderDetails.data.note}</div>
                    </div>

                    <div className='flex flex-wrap items-start'>
                        <label className='mr-2 font-bold'>Bounce Reason:</label>
                        <div>{orderDetails.data.bounce_reason || 'Unknown'}</div>
                    </div>

                    <div className='mt-3'>
                        <div>
                            <StanInput
                                topLabel='Send email to:'
                                type='email'
                                className='form-group'
                                placeholder='Email'
                                value={emailToSendTo}
                                {...register('email')}
                                error={!!errors.email}
                                hintText={errors.email?.message}
                                name='email'
                            />
                        </div>
                    </div>
                </div>
            </div>
        </StanModal>
    );
}
